/* ================================================================
 * Tour stop · The browser entry point
 * ----------------------------------------------------------------
 * See docs/code-roadmap.md §2 (frame trace) and §3.2 (ESM) for the
 * surrounding narrative.
 *
 * When you run `pnpm dev`, Vite starts a local HTTP server and
 * serves packages/client/index.html. That HTML file contains a
 * single <script type="module" src="..."> tag pointing at this
 * file. The browser fetches it as an ES module, the import chain
 * resolves automatically, and execution lands here. There is no
 * bootstrap, no init function — top-level code runs once when the
 * module loads.
 *
 * What this file actually does:
 *
 *  1. `document.getElementById("app")` reaches into the DOM and
 *     finds the <div id="app"> in index.html. That's our mount
 *     point. The Three.js stage appends its <canvas> as a child.
 *
 *  2. We bail loudly if the element is missing. With the
 *     noUncheckedIndexedAccess TypeScript flag on, the return type
 *     of getElementById is `HTMLElement | null`, so the truthy check
 *     is required before passing it into the App constructor (which
 *     expects HTMLElement, not nullable).
 *
 *  3. `new App({ parent }).start()` constructs the game and
 *     asynchronously boots it (renderer init is async — and WebGPU
 *     is a hard requirement; Stage.create throws without it).
 *     `void` in front of `start()` is a TypeScript convention
 *     meaning "I know this returns a Promise; I'm intentionally not
 *     awaiting it." This is a top-level script — there's nothing to
 *     await INTO.
 *
 *  4. `globalThis.__app = app` exposes the App instance on the
 *     global object. In a browser, `globalThis` is `window`. From
 *     the DevTools console, you can now type `__app.snapshot()` and
 *     poke at the live game. The Playwright visual smoke harness
 *     also uses this hook to script the running game from the
 *     outside. The `as unknown as { __app?: App }` cast is the
 *     standard TypeScript dance for adding an ad-hoc property to a
 *     globally-typed object.
 * ================================================================ */

import { App } from "./app/App.js";

const parent = document.getElementById("app");
if (!parent) throw new Error("missing #app element");

// Dev-only material lab (`?lab` or `?lab=<scene>`): boots the Three.js
// look-dev environment instead of the game. The dynamic import keeps the
// lab out of production builds.
const labParam = import.meta.env.DEV ? new URLSearchParams(location.search).get("lab") : null;
if (labParam !== null) {
  void import("./render/lab/material-lab.js").then((m) =>
    m.runLab(parent, labParam === "" ? "rink" : labParam),
  );
} else {
  const app = new App({ parent });
  void app.start();

  // Expose for debugging from DevTools.
  (globalThis as unknown as { __app?: App }).__app = app;
}
